import { LandingVariant } from '../../models/landing/variant';

/**
 * A single contact-centre OPENING window, expressed in the parent block's
 * timezone. Multiple windows let a locale describe e.g. different weekday and
 * Saturday hours (Italy: Mon–Fri 08:00–20:00 AND Sat 08:00–17:00).
 */
export type OpeningWindow = {
  /** Window start, 24h wall-clock `HH:MM` (e.g. "09:00"). */
  open: string;
  /** Window end, 24h wall-clock `HH:MM`. If `close <= open` the window is treated as overnight (spans midnight). */
  close: string;
  /** ISO weekdays the window applies to, 1=Monday … 7=Sunday. For an overnight window this is the day it OPENS on. */
  days: number[];
};

/**
 * A per-locale contact-centre schedule authored in the `<brand>-landing-variant`
 * PostHog payload. `hours` lists the OPENING windows (in `tz` — NOT the
 * visitor's timezone). While the current time falls OUTSIDE every open window
 * (i.e. the centre is closed) the landing page swaps to `lp` instead of the
 * locale's base experience. Editing this in the PostHog UI changes the live page
 * with no code deploy.
 */
export type AfterHours = {
  /** Which experience to show while the call centre is CLOSED (outside all `hours`). */
  lp: LandingVariant;
  /** IANA timezone the hours are expressed in, e.g. "Europe/Paris". */
  tz: string;
  /** The opening windows. Empty/missing => always closed (the `lp` always shows). */
  hours: OpeningWindow[];
};

const HHMM = /^(\d{2}):(\d{2})$/;

/** Parse "HH:MM" into minutes-since-midnight, or null if malformed / out of range. */
const toMinutes = (value: string): number | null => {
  const match = HHMM.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

/**
 * The wall-clock weekday (ISO, 1=Mon … 7=Sun) and minutes-since-midnight for
 * `now` as observed in `tz`. Uses the built-in Intl API so no date library /
 * timezone dependency is needed. Returns null if the timezone is invalid.
 */
const localParts = (now: Date, tz: string): { isoDay: number; minutes: number } | null => {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour12: false,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(now);
  } catch {
    // Invalid timezone identifier -> treat as "cannot evaluate schedule".
    return null;
  }

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  const isoDay = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 }[get('weekday')];
  if (!isoDay) return null;

  // Intl renders midnight as "24" in some environments; normalise to 0.
  const hour = Number(get('hour')) % 24;
  const minutes = hour * 60 + Number(get('minute'));
  return { isoDay, minutes };
};

/**
 * True when the given local weekday/time falls inside a single opening window.
 * Handles overnight windows (`close <= open`, e.g. 22:00→06:00) by attributing
 * the morning leg to the previous day's opening. A malformed window (bad time,
 * empty `days`) is simply not open.
 */
const isWithinWindow = (window: OpeningWindow, isoDay: number, minutes: number): boolean => {
  const open = toMinutes(window.open);
  const close = toMinutes(window.close);
  if (open === null || close === null || !window.days?.length) return false;

  if (open < close) {
    // Same-day window, e.g. 09:00–18:00.
    return window.days.includes(isoDay) && minutes >= open && minutes < close;
  }

  // Overnight window, e.g. 22:00–06:00, split across two calendar days;
  // `days` names the day the window OPENS on.
  if (minutes >= open) return window.days.includes(isoDay);
  if (minutes < close) {
    const previousDay = isoDay === 1 ? 7 : isoDay - 1;
    return window.days.includes(previousDay);
  }
  return false;
};

/**
 * True when the contact centre is CLOSED for `now`, evaluated in the schedule's
 * own timezone — i.e. `now` is outside EVERY opening window. When true the
 * landing page shows `config.lp` instead of the locale's base experience.
 *
 * Any unusable schedule (unknown timezone, no windows) is treated as CLOSED so
 * the closed-hours experience (typically the online link) is shown rather than
 * pushing visitors to a phone line that may be unattended. A malformed
 * individual window is skipped (counts as not-open).
 */
export const isClosed = (config: AfterHours, now: Date): boolean => {
  const local = localParts(now, config.tz);
  if (!local) return true; // unknown tz -> cannot confirm open -> treat as closed
  if (!config.hours?.length) return true; // no windows -> always closed

  const { isoDay, minutes } = local;
  const open = config.hours.some((window) => isWithinWindow(window, isoDay, minutes));
  return !open;
};
